import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRef } from "react";
import emailjs from "@emailjs/browser";
import {
  Building2,
  FileImage,
  Mail,
  Ruler,
  Sparkles,
  Upload,
  Video,
  X,
} from "lucide-react";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import LocationPicker from "@/components/LocationPicker";
import { getAuthSession, getValidAuthSession, signInWithPassword } from "@/lib/supabaseAuth";
import { fetchSupabaseRows } from "@/lib/supabaseRest";
import { mapSubmissionToProperty } from "@/lib/publishedProperties";

const initialForm = {
  perfil: "dueno",
  nombre: "",
  telefono: "",
  correo: "",
  precio: "",
  ubicacion: "",
  calle: "",
  numero: "",
  colonia: "",
  ciudad: "",
  codigoPostal: "",
  lat: "",
  lng: "",
  tipo: "",
  plan: "gratis",
  descripcion: "",
  ventajas: ["", "", ""],
  frente: "",
  fondo: "",
  metros: "",
  altura: "",
  nivel: "",
  nivelDetalle: "",
  estacionamiento: "",
  noMedidas: false,
  servicios: [],
  serviciosOtro: "",
  disponibilidad: "",
  disponibilidadOtro: "",
  contrato: "Mínimo 1 año",
  contratoOtro: "",
  zonaTipos: [],
  tieneRestricciones: "no",
  restricciones: "",
  negociosMismaPlaza: "",
};

const premiumAnalysisPlans = new Set([
  "pro",
  "premium",
  "impulso",
  "tulocalmx",
  "filtrado",
]);
const launchPromoActive = true;

const photoGuide = [
  "Fachada desde la calle",
  "Interior completo",
  "Baño",
  "Entrada o acceso",
  "Estacionamiento",
  "Vista de la calle o flujo",
  "Si es terreno: frente, laterales, fondo y esquina",
];

const propertyTypeOptions = [
  { value: "local", label: "Local" },
  { value: "bodega", label: "Bodega" },
  { value: "terreno", label: "Terreno" },
  { value: "oficina", label: "Oficina / consultorio" },
  { value: "casa", label: "Casa para negocio" },
];

const serviceOptions = ["Luz", "Agua", "Internet", "Drenaje", "Baño", "Aire acondicionado"];

const availabilityOptions = [
  "Inmediata",
  "En 15 días",
  "En 30 días",
  "Otro",
];
const contractOptions = ["Mínimo 1 año", "2 años", "3 años", "Otro"];
const zoneTypeOptions = ["Habitacional", "Comercial", "Industrial"];
const ownerContactStorageKey = "tulocalmx_owner_contact";

const cleanContactValue = (value) => String(value || "").trim();

const normalizeMexicanPhone = (value) => {
  let digits = String(value || "").replace(/\D/g, "");

  if (digits.length === 13 && digits.startsWith("521")) {
    digits = digits.slice(3);
  } else if (digits.length === 12 && digits.startsWith("52")) {
    digits = digits.slice(2);
  } else if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
};

const extractAddressNumber = (value = "") => {
  const match = String(value || "").match(/(?:^|[\s,#-])(\d+[A-Za-z]?(?:\s?[A-Za-z])?)(?=\s|,|#|-|$)/);
  return match?.[1]?.trim() || "";
};

const getSavedOwnerContact = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(ownerContactStorageKey) || "{}");
    return {
      nombre: cleanContactValue(saved.nombre),
      telefono: normalizeMexicanPhone(saved.telefono),
      correo: cleanContactValue(saved.correo),
    };
  } catch (error) {
    return { nombre: "", telefono: "", correo: "" };
  }
};

const saveOwnerContact = ({ nombre, telefono, correo }) => {
  const contact = {
    nombre: cleanContactValue(nombre),
    telefono: normalizeMexicanPhone(telefono),
    correo: cleanContactValue(correo),
  };

  if (!contact.nombre && !contact.telefono && !contact.correo) return;
  localStorage.setItem(ownerContactStorageKey, JSON.stringify(contact));
};

const normalizeAdvantages = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, 10);

const parseMeasure = (value) => {
  const normalized = (value || "")
    .toString()
    .replace(",", ".")
    .match(/[\d.]+/);

  return normalized ? Number(normalized[0]) : 0;
};

const formatSquareMeters = (value) => {
  if (!Number.isFinite(value) || value <= 0) return "";
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const hasValidCoordinates = (lat, lng) => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0)
  );
};

const buildFilePreview = (file) => {
  if (!file) return "";
  if (!file.type?.startsWith("image/") && !file.type?.startsWith("video/")) return "";
  return URL.createObjectURL(file);
};

const Registro = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authSession, setAuthSession] = useState(() => getAuthSession());
  const formRef = useRef(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [plans, setPlans] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedPropertyUrl, setSubmittedPropertyUrl] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(initialForm);
  const [ownerContactSuggestion, setOwnerContactSuggestion] = useState(() =>
    getSavedOwnerContact()
  );
  const planFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const plan = (params.get("plan") || "").toLowerCase().trim();
    if (launchPromoActive) return "pro";
    return premiumAnalysisPlans.has(plan) ? plan : "gratis";
  }, [location.search]);
  const checkoutSuccess = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("checkout") === "success" || params.get("payment") === "success";
  }, [location.search]);
  const checkoutSessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("session_id") || params.get("payment_intent") || "";
  }, [location.search]);
  const launchPromo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return launchPromoActive || params.get("promo") === "primeras100";
  }, [location.search]);
  const priceAmount = Number(form.precio);
  const isEconomicPrice =
    Number.isFinite(priceAmount) && priceAmount >= 2000 && priceAmount <= 8000;
  const isProPlan = premiumAnalysisPlans.has(planFromUrl);

  useEffect(() => {
    setForm((prev) => ({ ...prev, plan: planFromUrl }));
  }, [planFromUrl]);

  useEffect(() => {
    let isMounted = true;

    getValidAuthSession().then((session) => {
      if (isMounted && session?.access_token) {
        setAuthSession(session);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const contact = {
      nombre: form.nombre,
      telefono: form.telefono,
      correo: form.correo,
    };
    if (!contact.nombre || !contact.telefono || !contact.correo) return;

    saveOwnerContact(contact);
    setOwnerContactSuggestion(contact);
  }, [form.nombre, form.telefono, form.correo]);

  useEffect(() => {
    let isMounted = true;

    const metadata = authSession?.user?.user_metadata || {};
    const sessionContact = {
      nombre:
        metadata.nombre ||
        metadata.full_name ||
        metadata.name ||
        "",
      telefono: metadata.telefono || metadata.phone || "",
      correo: authSession?.user?.email || metadata.email || "",
    };

    const applyContact = (contact) => {
      const cleanContact = {
        nombre: cleanContactValue(contact.nombre),
        telefono: normalizeMexicanPhone(contact.telefono),
        correo: cleanContactValue(contact.correo),
      };

      if (!cleanContact.nombre && !cleanContact.telefono && !cleanContact.correo) return;
      setOwnerContactSuggestion(cleanContact);
      setForm((prev) => ({
        ...prev,
        nombre: prev.nombre || cleanContact.nombre,
        telefono: prev.telefono || cleanContact.telefono,
        correo: prev.correo || cleanContact.correo,
      }));
    };

    const loadPreviousContact = async () => {
      const savedContact = getSavedOwnerContact();
      applyContact({ ...savedContact, ...sessionContact });

      if (!authSession?.access_token || !authSession?.user?.id) return;

      try {
        const query = new URLSearchParams({
          select: "nombre,telefono,correo",
          user_id: `eq.${authSession.user.id}`,
          order: "created_at.desc",
          limit: "1",
        });
        const rows = await fetchSupabaseRows(
          "property_submissions",
          query.toString(),
          { accessToken: authSession.access_token }
        );
        const lastContact = rows?.[0];
        if (!isMounted || !lastContact) return;

        const preferredContact = {
          nombre: lastContact.nombre || sessionContact.nombre || savedContact.nombre,
          telefono: lastContact.telefono || sessionContact.telefono || savedContact.telefono,
          correo: lastContact.correo || sessionContact.correo || savedContact.correo,
        };
        saveOwnerContact(preferredContact);
        applyContact(preferredContact);
      } catch (error) {
        if (isMounted) {
          console.log("No se pudo cargar el contacto anterior:", error);
        }
      }
    };

    loadPreviousContact();

    return () => {
      isMounted = false;
    };
  }, [authSession?.access_token, authSession?.user?.id, authSession?.user?.email, authSession?.user?.user_metadata]);

  useEffect(() => {
    if (!checkoutSuccess || !checkoutSessionId) return;

    const sentKey = `tulocalmx_pro_payment_email_${checkoutSessionId}`;
    if (sessionStorage.getItem(sentKey)) return;

    sessionStorage.setItem(sentKey, "1");
    fetch("/api/send-pro-payment-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ sessionId: checkoutSessionId }),
    }).catch(() => {});
  }, [checkoutSuccess, checkoutSessionId]);

  const updateForm = (field, value) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[field];
      return nextErrors;
    });

    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "frente" || field === "fondo") {
        const frente = parseMeasure(field === "frente" ? value : next.frente);
        const fondo = parseMeasure(field === "fondo" ? value : next.fondo);
        next.metros = formatSquareMeters(frente * fondo);
      }

      return next;
    });
  };

  const fieldClass = (field, base = "") =>
    `${base} ${
      fieldErrors[field]
        ? "border-red-400 bg-red-50 ring-2 ring-red-100 focus:border-red-500"
        : ""
    }`;

  const fieldError = (field) =>
    fieldErrors[field] ? (
      <p className="mt-1 text-xs font-bold text-red-600">{fieldErrors[field]}</p>
    ) : null;

  const scrollToField = (field) => {
    window.setTimeout(() => {
      document
        .querySelector(`[data-field="${field}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const validateBeforeSubmit = () => {
    const errors = {};
    const requireText = (field, label) => {
      if (!String(form[field] || "").trim()) errors[field] = `${label} es obligatorio.`;
    };

    requireText("nombre", "Tu nombre");
    requireText("telefono", "WhatsApp");
    const cleanPhone = normalizeMexicanPhone(form.telefono);
    if (form.telefono && cleanPhone.length !== 10) {
      errors.telefono = "Escribe un WhatsApp de 10 digitos.";
    }
    requireText("correo", "Correo electrónico");
    requireText("precio", "Renta mensual");
    requireText("tipo", "Tipo real de propiedad");
    requireText("ubicacion", "Ubicación");
    if (!hasValidCoordinates(form.lat, form.lng)) {
      errors.ubicacion = "Busca y marca la ubicación exacta en el mapa.";
    }
    requireText("calle", "Calle");
    requireText("colonia", "Colonia");
    requireText("frente", "Frente");
    requireText("fondo", "Fondo");
    requireText("metros", "Metros cuadrados");
    requireText("nivel", "Nivel o piso");
    if (form.nivel === "otro") requireText("nivelDetalle", "Detalle del nivel");
    requireText("estacionamiento", "Estacionamiento");
    if (!form.servicios.length && !form.serviciosOtro.trim()) {
      errors.servicios = "Selecciona o escribe al menos un servicio.";
    }
    requireText("disponibilidad", "Disponibilidad");
    if (form.disponibilidad === "Otro") {
      requireText("disponibilidadOtro", "Detalle de disponibilidad");
    }
    requireText("contrato", "Contrato");
    if (form.contrato === "Otro") {
      requireText("contratoOtro", "Detalle de contrato");
    }
    if (!form.zonaTipos.length) {
      errors.zonaTipos = "Selecciona al menos un tipo de zona.";
    }
    if (form.tieneRestricciones === "si") {
      requireText("restricciones", "Restricciones");
    }
    requireText("negociosMismaPlaza", "Entorno de la propiedad");
    if (form.descripcion.trim().length < 40) {
      errors.descripcion = "Escribe una descripción de al menos 40 caracteres.";
    }
    if (normalizeAdvantages(form.ventajas).length < 3) {
      errors.ventajas = "Agrega mínimo 3 ventajas principales.";
    }
    if (images.length < 3) {
      errors.imagenes = "Sube mínimo 3 fotografías de la propiedad.";
    }

    setFieldErrors(errors);
    const firstField = Object.keys(errors)[0];
    if (firstField) {
      scrollToField(firstField);
      alert(`Falta completar: ${errors[firstField]}`);
      return false;
    }

    return true;
  };

  const toggleService = (service) => {
    setForm((prev) => {
      const current = Array.isArray(prev.servicios) ? prev.servicios : [];
      const exists = current.includes(service);

      return {
        ...prev,
        servicios: exists
          ? current.filter((item) => item !== service)
          : [...current, service],
      };
    });
  };

  const appendLimitedFiles = (setter, files, limit) => {
    setter((prev) => [...prev, ...Array.from(files)].slice(0, limit));
  };

  const removeFile = (setter, indexToRemove) => {
    setter((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const uploadFiles = async (files, resourceType = "auto") => {
    const urls = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "tulocalmx_upload");

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/dz2ylmtpr/${resourceType}/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) {
        throw new Error("No se pudo subir un archivo");
      }

      const data = await res.json();
      urls.push(data.secure_url);
    }

    return urls;
  };

  const buildMeasuresSummary = () => {
    if (form.noMedidas) {
      return "No tiene medidas exactas. Pendiente de validar con plano, croquis o visita.";
    }

    return [
      form.frente ? `Frente: ${form.frente}` : "",
      form.fondo ? `Fondo: ${form.fondo}` : "",
      form.metros ? `Metros: ${form.metros}` : "",
      form.altura ? `Altura: ${form.altura}` : "",
      form.nivel
        ? `Nivel: ${form.nivel === "otro" ? form.nivelDetalle : form.nivel}`
        : "",
      form.estacionamiento ? `Estacionamiento: ${form.estacionamiento}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const telefonoFinal = normalizeMexicanPhone(form.telefono);
    if (telefonoFinal !== form.telefono) {
      updateForm("telefono", telefonoFinal);
    }

    if (!validateBeforeSubmit()) return;

    const currentSession = await getValidAuthSession();
    if (currentSession?.access_token) {
      setAuthSession(currentSession);
    }

    if (!currentSession?.access_token || !currentSession?.user?.id) {
      setLoginError("");
      setShowLogin(true);
      return;
    }

    if (!hasValidCoordinates(form.lat, form.lng)) {
      alert("Marca la ubicacion exacta en el mapa para publicar tu propiedad.");
      return;
    }

    setIsSubmitting(true);
    let propertyWasProcessed = false;
    let processedPropertyFallbackUrl = "/mis-propiedades?estado=revision";

    try {
      setSubmittedPropertyUrl("");
      const imageUrls = await uploadFiles(images, "image");
      const videoUrls = await uploadFiles(videos, "video");
      const planUrls = await uploadFiles(plans, "auto");
      const serviciosFinal = [
        ...(Array.isArray(form.servicios) ? form.servicios : []),
        form.serviciosOtro.trim(),
      ]
        .filter(Boolean)
        .join(", ");
      const disponibilidadFinal =
        form.disponibilidad === "Otro"
          ? form.disponibilidadOtro.trim()
          : form.disponibilidad;
      const contratoFinal =
        form.contrato === "Otro" ? form.contratoOtro.trim() : form.contrato;
      const zonaTipoFinal = form.zonaTipos.join(" y ");
      const contactoDirecto = "WhatsApp del anunciante";
      const comisionTulocalmx = "No solicitada";
      const medidas = buildMeasuresSummary();
      const ventajasFinal = normalizeAdvantages(form.ventajas).join("\n");
      const tipoPublicacion = isEconomicPrice ? "economico" : form.tipo;
      const tipoReal =
        propertyTypeOptions.find((option) => option.value === form.tipo)?.label ||
        form.tipo;
      const nivelFinal = form.nivel === "otro" ? form.nivelDetalle : form.nivel;
      const formForReview = {
        ...form,
        telefono: telefonoFinal,
        plan: planFromUrl,
        nivel: nivelFinal,
        servicios: serviciosFinal,
        disponibilidad: disponibilidadFinal,
        contrato: contratoFinal,
        zona_tipo: zonaTipoFinal,
      };
      const restricciones = form.tieneRestricciones === "si"
        ? form.restricciones
        : "No hay restricciones reportadas por el anunciante.";
      const submissionId = crypto.randomUUID();
      const propertyUrl = `${window.location.origin}/propiedad/supabase-${submissionId}`;

      const submission = {
        id: submissionId,
        plan: planFromUrl,
        tipo_anunciante: form.perfil,
        nombre: form.nombre,
        telefono: telefonoFinal,
        correo: form.correo,
        precio: form.precio ? Number(form.precio) : null,
        ubicacion: form.ubicacion,
        calle: form.calle,
        numero: form.numero,
        colonia: form.colonia,
        ciudad: form.ciudad,
        codigo_postal: form.codigoPostal,
        lat: Number(form.lat),
        lng: Number(form.lng),
        map_url: `https://www.google.com/maps?q=${Number(form.lat)},${Number(form.lng)}&z=18&output=embed`,
        tipo: tipoPublicacion,
        descripcion: form.descripcion,
        ventajas: ventajasFinal,
        frente: form.frente,
        fondo: form.fondo,
        metros: form.metros,
        altura: form.altura,
        nivel: nivelFinal,
        estacionamiento: form.estacionamiento,
        servicios: serviciosFinal,
        disponibilidad: disponibilidadFinal,
        contrato: contratoFinal,
        zona_tipo: zonaTipoFinal,
        restricciones,
        negocios_misma_plaza: form.negociosMismaPlaza,
        ayuda_tulocalmx: false,
        contacto_directo: contactoDirecto,
        comision_tulocalmx: comisionTulocalmx,
        medidas,
        imagenes: imageUrls,
        videos: videoUrls,
        planos: planUrls,
      };

      const agentResponse = await fetch("/api/submit-property", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          submission,
          formForReview,
          media: {
            imageUrls,
            videoUrls,
            planUrls,
          },
          typeReal: tipoReal,
          tipoReal,
          propertyUrl,
        }),
      });

      const agentText = await agentResponse.text();
      let agentResult = {};
      try {
        agentResult = agentText ? JSON.parse(agentText) : {};
      } catch (error) {
        agentResult = { detail: agentText };
      }
      if (!agentResponse.ok) {
        throw new Error(
          agentResult.detail ||
            agentResult.error ||
            `No se pudo procesar la propiedad con el agente. Codigo ${agentResponse.status}.`
        );
      }

      const review = agentResult.review;
      propertyWasProcessed = true;
      const publishedPropertyForNavigation = mapSubmissionToProperty({
        ...submission,
        estado_publicacion: review.estadoPublicacion,
        pago_estado: review.pagoEstado,
        revision_score: review.score,
        revision_alertas: review.alerts || [],
        publicada_en:
          review.estadoPublicacion === "publicada" ? new Date().toISOString() : null,
      });
      const clientDestinationUrl =
        review.estadoPublicacion === "publicada"
          ? propertyUrl
          : `${window.location.origin}/mis-propiedades`;
      processedPropertyFallbackUrl = "/mis-propiedades?estado=revision";
      const clientCtaText =
        review.estadoPublicacion === "publicada"
          ? "Ver mi publicacion"
          : "Revisar mi propiedad";

      await emailjs.send(
        "service_h82m5ln",
        "template_jpxs5ip",
        {
          to_email: form.correo,
          asunto: review.needsManualReview
            ? "Tu propiedad fue recibida y esta en revision"
            : "Tu propiedad fue publicada en Tulocalmx",
          nombre: form.nombre,
          telefono: telefonoFinal,
          correo: form.correo,
          tipo_anunciante: form.perfil,
          precio: form.precio,
          ubicacion: form.ubicacion,
          tipo: tipoPublicacion,
          tipo_real: tipoReal,
          contacto_directo: contactoDirecto,
          comision_tulocalmx: comisionTulocalmx,
          descripcion: form.descripcion,
          ventajas: ventajasFinal,
          servicios: serviciosFinal,
          disponibilidad: disponibilidadFinal,
          contrato: contratoFinal,
          zona_tipo: zonaTipoFinal,
          restricciones,
          negocios_misma_plaza: form.negociosMismaPlaza,
          medidas,
          estado_revision: review.needsManualReview
            ? `REQUIERE REVISION MANUAL: ${(review.blockers || []).join("; ")}`
            : `PUBLICADA AUTOMATICAMENTE: ${(review.reminders || []).join("; ") || "sin alertas"}`,
          mensaje_cliente: review.needsManualReview
            ? "Recibimos tu propiedad. Necesita una revision rapida de Tulocalmx antes de publicarse. Puedes revisar o editar los datos desde tu panel."
            : launchPromo
            ? "Recibimos tu propiedad y quedo publicada automaticamente con Plan Pro gratis por 1 mes dentro de la promocion de lanzamiento."
            : "Recibimos tu propiedad y quedo publicada automaticamente. Ya puedes verla desde tu panel de propiedades.",
          link_cliente: clientDestinationUrl,
          texto_boton_cliente: clientCtaText,
          resumen_cliente: review.needsManualReview
            ? "Tu propiedad esta en revision. Entra a Mis propiedades para ver el motivo y editarla si hace falta."
            : "Tu propiedad ya esta publicada. Puedes abrir el enlace para verla como la veran los interesados.",
          revision_alertas: review.alerts.join("\n"),
          revision_score: review.score,
          imagenes: imageUrls.join("\n"),
          videos: videoUrls.join("\n"),
          planos: planUrls.join("\n"),
          url_publicacion:
            review.estadoPublicacion === "publicada" ? propertyUrl : "",
        },
        "nDzg6xYGN7OO8id3F"
      );

      const whatsappText = review.needsManualReview
        ? `Hola ${form.nombre}, recibimos tu propiedad en Tulocalmx. Necesita una revision rapida antes de publicarse. Motivo: ${
            (review.blockers || []).slice(0, 2).join(", ") ||
            "hay datos que necesitan validacion"
          }. Tu registro incluye Plan Pro gratis por 1 mes si queda dentro de las primeras 100 propiedades. Puedes revisarla desde tu panel.`
        : `Hola ${form.nombre}, recibimos tu propiedad en Tulocalmx y quedo publicada automaticamente${
            launchPromo ? " con Plan Pro gratis por 1 mes" : ""
          }. Ya puedes verla desde tu panel de propiedades.${
            propertyUrl ? ` Publicacion: ${propertyUrl}` : ""
          }`;
      const mensaje = encodeURIComponent(
        whatsappText
      );
      const telefono = "521" + telefonoFinal;
      const img = new Image();
      img.src = `https://api.callmebot.com/whatsapp.php?phone=${telefono}&text=${mensaje}&apikey=1340098`;

      const reviewReasons = [...new Set([...(review.blockers || []), ...(review.alerts || [])])]
        .filter(Boolean)
        .slice(0, 3)
        .join(" ");

      alert(
        review.needsManualReview
          ? `Tu propiedad fue recibida. Necesita una revision rapida de Tulocalmx antes de publicarse. Motivo: ${reviewReasons || "hay datos que necesitan validacion."}`
          : "Tu propiedad fue recibida y quedo publicada automaticamente. Si falta video o croquis, te lo recordaremos despues."
      );
      if (review.estadoPublicacion === "publicada") {
        setSubmittedPropertyUrl(propertyUrl);
      }
      setForm({ ...initialForm, plan: planFromUrl });
      setImages([]);
      setVideos([]);
      setPlans([]);
      navigate(
        review.estadoPublicacion === "publicada"
          ? `/propiedad/supabase-${submissionId}`
          : "/mis-propiedades?estado=revision",
        {
          replace: true,
          state:
            review.estadoPublicacion === "publicada"
              ? { publishedProperty: publishedPropertyForNavigation }
              : undefined,
        }
      );
    } catch (error) {
      console.log("ERROR REGISTRO:", error);
      alert(
        `No se pudo enviar la propiedad. Detalle: ${
          error.message || "Intenta de nuevo."
        }`
      );
      navigate(
        propertyWasProcessed
          ? processedPropertyFallbackUrl
          : "/mis-propiedades?estado=revision",
        { replace: true }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginAndSubmit = async (event) => {
    event.preventDefault();
    setIsLoggingIn(true);
    setLoginError("");

    try {
      const session = await signInWithPassword({
        email: loginEmail,
        password: loginPassword,
        remember: true,
      });
      setAuthSession(session);
      setShowLogin(false);
      window.setTimeout(() => formRef.current?.requestSubmit(), 0);
    } catch (error) {
      setLoginError(error.message || "No se pudo iniciar sesion.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="mx-auto max-w-6xl px-4 pb-12">
        <div className="relative mb-6 flex items-center justify-between gap-4 pr-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#F97316]">
              Registra tu espacio
            </p>
            <h1 className="text-2xl font-black text-slate-950 sm:text-3xl">
              {isProPlan ? "Publica con Plan Pro" : "Publicar tu propiedad es gratis"}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              {isProPlan
                ? "Incluye contacto directo, análisis de zona (IA), asesor (IA) y leads perfilados directo a tu WhatsApp."
                : "Contacto directo a tu WhatsApp."}
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(authSession?.access_token ? "/mi-perfil" : "/")}
            aria-label="Salir"
            className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
          >
            <X size={18} />
          </button>
        </div>

        <div className={`mb-5 flex flex-col gap-4 rounded-xl border bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between ${
          isProPlan ? "border-orange-200 bg-orange-50/40" : "border-orange-100"
        }`}>
          <div className="flex items-start gap-3">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
              isProPlan ? "bg-orange-500 text-white" : "bg-orange-50 text-[#F97316]"
            }`}>
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-lg font-black text-slate-950">
                  {isProPlan ? "Plan Pro" : "Hazte Pro"}
                </p>
                {isProPlan && (
                  <span className="rounded-full bg-orange-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-orange-700">
                    {launchPromo ? "Incluido" : "Activo"}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm font-black text-orange-700">
                {launchPromo
                  ? "Promoción para las primeras 100 propiedades"
                  : "Análisis de zona (IA) + Asesor (IA) + Leads perfilados directo a tu WhatsApp"}
              </p>
              <p className="mt-1 max-w-2xl text-sm font-semibold leading-6 text-slate-700">
                {isProPlan
                  ? launchPromo
                    ? "Tu propiedad disfrutará sin costo los beneficios Pro: análisis de zona (IA), asesor (IA) y leads perfilados directo a tu WhatsApp."
                    : "Tu publicación generará análisis de zona (IA), asesor (IA) y seguimiento para conectar leads perfilados directo a tu WhatsApp."
                  : "Recibe personas con interés real en tu WhatsApp, apoyadas por análisis de zona (IA) y asesor (IA)."}
              </p>
            </div>
          </div>
          {!isProPlan && (
            <button
              type="button"
              onClick={() => navigate("/plan-pro")}
              className="inline-flex shrink-0 items-center justify-center rounded-lg bg-[#F97316] px-4 py-3 text-sm font-black text-white transition hover:bg-orange-600"
            >
              Ver Plan Pro
            </button>
          )}
        </div>

        {checkoutSuccess && isProPlan && (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
            <p className="text-lg font-black text-emerald-950">
              Pago aceptado. Tu Plan Pro está activo.
            </p>
            <p className="mt-2 text-sm font-semibold leading-6 text-emerald-900">
              Ya puedes completar tu propiedad. Al enviarla, el sistema activará
              el análisis de zona, el asesor IA y la publicación Pro.
            </p>
          </div>
        )}

        {!authSession?.access_token && (
          <div className="mb-5 rounded-xl border border-orange-100 bg-orange-50 p-5 text-center">
            <p className="font-black text-orange-950">
              Para publicar necesitas una cuenta segura.
            </p>
            <p className="mt-2 text-sm leading-6 text-orange-900">
              Así podrás ver, editar, pausar y administrar tus propiedades desde tu portal.
            </p>
            <div className="mt-4 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate("/crear-cuenta")}
                className="rounded-lg bg-[#F97316] px-4 py-3 text-sm font-black text-white"
              >
                Crear cuenta
              </button>
              <button
                type="button"
                onClick={() => navigate("/iniciar-sesion")}
                className="rounded-lg border border-orange-200 bg-white px-4 py-3 text-sm font-black text-orange-900"
              >
                Iniciar sesion
              </button>
            </div>
          </div>
        )}

        {submittedPropertyUrl && (
          <div className="mb-5 rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-center">
            <p className="text-sm font-black text-emerald-900">
              Tu propiedad ya está publicada.
            </p>
            <a
              href={submittedPropertyUrl}
              className="mt-3 inline-flex rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white transition hover:bg-emerald-700"
            >
              Ver tu publicación
            </a>
          </div>
        )}

        <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-5">
          <div className="grid gap-5 xl:grid-cols-2">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#0056D2]" />
                <h2 className="font-bold text-slate-950">Datos principales</h2>
              </div>
              <p className="mb-4 text-xs font-semibold text-slate-500">
                Los campos marcados con <span className="text-[#F97316]">*</span> son obligatorios.
              </p>

              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  data-field="nombre"
                  required
                  type="text"
                  id="owner-name"
                  name="name"
                  autoComplete="name"
                  list="owner-name-suggestions"
                  placeholder="Tu nombre *"
                  value={form.nombre}
                  onChange={(e) => updateForm("nombre", e.target.value)}
                  className={fieldClass("nombre", "rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100")}
                />
                <datalist id="owner-name-suggestions">
                  {ownerContactSuggestion.nombre && (
                    <option value={ownerContactSuggestion.nombre} />
                  )}
                </datalist>

                <input
                  data-field="telefono"
                  required
                  type="tel"
                  id="owner-phone"
                  name="tel"
                  autoComplete="tel"
                  list="owner-phone-suggestions"
                  placeholder="WhatsApp (10 digitos) *"
                  value={form.telefono}
                  onChange={(e) => updateForm("telefono", normalizeMexicanPhone(e.target.value))}
                  className={fieldClass("telefono", "rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100")}
                />
                <datalist id="owner-phone-suggestions">
                  {ownerContactSuggestion.telefono && (
                    <option value={ownerContactSuggestion.telefono} />
                  )}
                </datalist>

                <div data-field="correo" className="relative sm:col-span-2">
                  <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                  <input
                    required
                    type="email"
                    id="owner-email"
                    name="email"
                    autoComplete="email"
                    list="owner-email-suggestions"
                    placeholder="Correo electronico *"
                    value={form.correo}
                    onChange={(e) => updateForm("correo", e.target.value)}
                    className={fieldClass("correo", "w-full rounded-lg border border-slate-200 p-3 pl-9 outline-none focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100")}
                  />
                  <datalist id="owner-email-suggestions">
                    {ownerContactSuggestion.correo && (
                      <option value={ownerContactSuggestion.correo} />
                    )}
                  </datalist>
                  {fieldError("correo")}
                </div>

                <input
                  data-field="precio"
                  required
                  type="number"
                  placeholder="$ Renta mensual *"
                  value={form.precio}
                  onChange={(e) => updateForm("precio", e.target.value)}
                  className={fieldClass("precio", "rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100")}
                />

                {isEconomicPrice && (
                  <div className="sm:col-span-2">
                    <div className="rounded-lg border border-orange-100 bg-orange-50 p-3 text-sm font-semibold leading-5 text-orange-900">
                      Tu propiedad está en el rango de $2,000 a $8,000, se publicará en Accesibles para que la encuentren más fácil.
                    </div>
                  </div>
                )}

                <select
                  data-field="tipo"
                  required
                  value={form.tipo}
                  onChange={(e) => updateForm("tipo", e.target.value)}
                  className={fieldClass("tipo", "rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100")}
                >
                  <option value="">Tipo real *</option>
                  {propertyTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div data-field="ubicacion" className={fieldErrors.ubicacion ? "rounded-xl ring-2 ring-red-200" : ""}>
                <LocationPicker
                  address={form.ubicacion}
                  lat={form.lat}
                  lng={form.lng}
                  onAddressChange={(value) => updateForm("ubicacion", value)}
                  onSelect={({ lat, lng }) => {
                    updateForm("lat", lat);
                    updateForm("lng", lng);
                    setFieldErrors((prev) => {
                      const nextErrors = { ...prev };
                      delete nextErrors.ubicacion;
                      return nextErrors;
                    });
                  }}
                  onAddressDetails={(details) => {
                    const fallbackNumber = extractAddressNumber(form.ubicacion || details.displayName);
                    updateForm("calle", details.calle || "");
                    updateForm("numero", details.numero || fallbackNumber || "");
                    updateForm("colonia", details.colonia || "");
                    updateForm("ciudad", details.ciudad || "");
                    updateForm("codigoPostal", details.codigoPostal || "");
                    setFieldErrors((prev) => {
                      const nextErrors = { ...prev };
                      delete nextErrors.calle;
                      delete nextErrors.colonia;
                      return nextErrors;
                    });
                  }}
                />
                {fieldError("ubicacion")}
                <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                  <p className="text-sm font-black text-emerald-950">
                    Confirma que esta ubicación esté bien
                  </p>
                  <p className="mt-1 text-sm leading-5 text-emerald-900">
                    Intentamos llenar calle, número y colonia con el pin. Si algo está mal, corrígelo aquí antes de enviar.
                  </p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-xs font-black uppercase tracking-wide text-emerald-900">
                        Calle *
                      </span>
                      <input
                        data-field="calle"
                        required
                        type="text"
                        value={form.calle}
                        onChange={(e) => updateForm("calle", e.target.value)}
                        className={fieldClass("calle", "mt-1 w-full rounded-lg border border-emerald-200 bg-white p-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100")}
                      />
                      {fieldError("calle")}
                    </label>
                    <label className="block">
                      <span className="text-xs font-black uppercase tracking-wide text-emerald-900">
                        Número
                      </span>
                      <input
                        type="text"
                        value={form.numero}
                        onChange={(e) => updateForm("numero", e.target.value)}
                        placeholder="Si no tiene, déjalo vacío"
                        className="mt-1 w-full rounded-lg border border-emerald-200 bg-white p-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs font-black uppercase tracking-wide text-emerald-900">
                        Colonia *
                      </span>
                      <input
                        data-field="colonia"
                        required
                        type="text"
                        value={form.colonia}
                        onChange={(e) => updateForm("colonia", e.target.value)}
                        className={fieldClass("colonia", "mt-1 w-full rounded-lg border border-emerald-200 bg-white p-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100")}
                      />
                      {fieldError("colonia")}
                    </label>
                    <label className="block">
                      <span className="text-xs font-black uppercase tracking-wide text-emerald-900">
                        Ciudad
                      </span>
                      <input
                        type="text"
                        value={form.ciudad}
                        onChange={(e) => updateForm("ciudad", e.target.value)}
                        className="mt-1 w-full rounded-lg border border-emerald-200 bg-white p-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                      />
                    </label>
                  </div>
                </div>
              </div>

            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <Ruler className="h-5 w-5 text-[#0056D2]" />
                <h2 className="font-bold text-slate-950">Medidas y operacion</h2>
              </div>
              <p className="mb-4 text-xs font-semibold text-slate-500">
                Agrega medidas reales para que la propiedad quede completa.
              </p>

              <div className="grid gap-3 sm:grid-cols-3">
                <input
                  data-field="frente"
                  required
                  type="text"
                  placeholder="Frente *"
                  value={form.frente}
                  onChange={(e) => updateForm("frente", e.target.value)}
                  className={fieldClass("frente", "rounded-lg border border-slate-200 p-3 outline-none")}
                />
                <input
                  data-field="fondo"
                  required
                  type="text"
                  placeholder="Fondo *"
                  value={form.fondo}
                  onChange={(e) => updateForm("fondo", e.target.value)}
                  className={fieldClass("fondo", "rounded-lg border border-slate-200 p-3 outline-none")}
                />
                <input
                  data-field="metros"
                  required
                  type="text"
                  placeholder="Metros cuadrados *"
                  value={form.metros}
                  readOnly
                  className={fieldClass("metros", "rounded-lg border border-slate-200 bg-slate-100 p-3 text-sm outline-none placeholder:text-xs")}
                />
                <input
                  data-field="altura"
                  type="text"
                  placeholder="Altura (opcional)"
                  value={form.altura}
                  onChange={(e) => updateForm("altura", e.target.value)}
                  className={fieldClass("altura", "rounded-lg border border-slate-200 p-3 outline-none")}
                />
                <select
                  data-field="nivel"
                  required
                  value={form.nivel}
                  onChange={(e) => updateForm("nivel", e.target.value)}
                  className={fieldClass("nivel", "rounded-lg border border-slate-200 p-3 outline-none")}
                >
                  <option value="">Nivel o piso *</option>
                  <option value="Planta baja">Planta baja</option>
                  <option value="Planta alta">Planta alta</option>
                  <option value="A pie de carretera">A pie de carretera</option>
                  <option value="otro">Otro / escribir</option>
                </select>
                {form.nivel === "otro" && (
                  <input
                    data-field="nivelDetalle"
                    required
                    type="text"
                    placeholder="Escribe el nivel, piso o acceso *"
                    value={form.nivelDetalle}
                    onChange={(e) => updateForm("nivelDetalle", e.target.value)}
                    className={fieldClass("nivelDetalle", "rounded-lg border border-slate-200 p-3 outline-none")}
                  />
                )}
                <input
                  data-field="estacionamiento"
                  required
                  type="text"
                  placeholder="Estacionamiento *"
                  value={form.estacionamiento}
                  onChange={(e) => updateForm("estacionamiento", e.target.value)}
                  className={fieldClass("estacionamiento", "rounded-lg border border-slate-200 p-3 outline-none")}
                />
              </div>

              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <div data-field="servicios" className={`rounded-xl border bg-slate-50 p-4 ${fieldErrors.servicios ? "border-red-400 ring-2 ring-red-100" : "border-slate-200"}`}>
                  <p className="text-sm font-bold text-slate-900">
                    Servicios disponibles *
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {serviceOptions.map((service) => (
                      <label
                        key={service}
                        className={`flex min-w-0 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-bold leading-tight ${
                          form.servicios.includes(service)
                            ? "border-[#0056D2] bg-blue-50 text-blue-950"
                            : "border-slate-200 bg-white text-slate-700"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.servicios.includes(service)}
                          onChange={() => toggleService(service)}
                          className="shrink-0"
                        />
                        <span className="min-w-0 break-words">{service}</span>
                      </label>
                    ))}
                  </div>
                  <input
                    type="text"
                    placeholder="Otro servicio o detalle"
                    value={form.serviciosOtro}
                    onChange={(e) => updateForm("serviciosOtro", e.target.value)}
                    className="mt-3 w-full rounded-lg border border-slate-200 bg-white p-3 outline-none"
                  />
                  {fieldError("servicios")}
                </div>

                <div data-field="disponibilidad" className={`rounded-xl border bg-slate-50 p-4 ${fieldErrors.disponibilidad || fieldErrors.disponibilidadOtro ? "border-red-400 ring-2 ring-red-100" : "border-slate-200"}`}>
                  <p className="text-sm font-bold text-slate-900">
                    Disponibilidad *
                  </p>
                  <select
                    required
                    value={form.disponibilidad}
                    onChange={(e) => updateForm("disponibilidad", e.target.value)}
                    className="mt-3 w-full rounded-lg border border-slate-200 bg-white p-3 outline-none"
                  >
                    <option value="">Elige una opción</option>
                    {availabilityOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {form.disponibilidad === "Otro" && (
                    <input
                      required
                      type="text"
                      placeholder="Escribe la disponibilidad"
                      value={form.disponibilidadOtro}
                      onChange={(e) => updateForm("disponibilidadOtro", e.target.value)}
                      className="mt-3 w-full rounded-lg border border-slate-200 bg-white p-3 outline-none"
                    />
                  )}
                  {fieldError("disponibilidad") || fieldError("disponibilidadOtro")}
                </div>
                <div data-field="contrato" className={`rounded-xl border bg-slate-50 p-4 ${fieldErrors.contrato || fieldErrors.contratoOtro ? "border-red-400 ring-2 ring-red-100" : "border-slate-200"}`}>
                  <p className="text-sm font-bold text-slate-900">Contrato *</p>
                  <select
                    required
                    value={form.contrato}
                    onChange={(e) => updateForm("contrato", e.target.value)}
                    className="mt-3 w-full rounded-lg border border-slate-200 bg-white p-3 outline-none"
                  >
                    {contractOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {form.contrato === "Otro" && (
                    <input
                      required
                      type="text"
                      placeholder="Ejemplo: 5 años"
                      value={form.contratoOtro}
                      onChange={(e) => updateForm("contratoOtro", e.target.value)}
                      className="mt-3 w-full rounded-lg border border-slate-200 bg-white p-3 outline-none"
                    />
                  )}
                  {fieldError("contrato") || fieldError("contratoOtro")}
                </div>
                <div data-field="zonaTipos" className={`rounded-xl border bg-slate-50 p-4 sm:col-span-2 ${fieldErrors.zonaTipos ? "border-red-400 ring-2 ring-red-100" : "border-slate-200"}`}>
                  <p className="text-sm font-bold text-slate-900">Zona *</p>
                  <p className="mt-1 text-sm leading-5 text-slate-600">
                    Selecciona una o varias. Esto se mostrará en la ficha como tipo de zona.
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    {zoneTypeOptions.map((option) => {
                      const checked = form.zonaTipos.includes(option);
                      return (
                        <label
                          key={option}
                          className={`cursor-pointer rounded-lg border px-3 py-2 text-sm font-bold ${
                            checked
                              ? "border-[#0056D2] bg-blue-50 text-blue-950"
                              : "border-slate-200 bg-white text-slate-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = checked
                                ? form.zonaTipos.filter((item) => item !== option)
                                : [...form.zonaTipos, option];
                              updateForm("zonaTipos", next);
                            }}
                            className="mr-2"
                          />
                          {option}
                        </label>
                      );
                    })}
                  </div>
                  {fieldError("zonaTipos")}
                </div>
              </div>

              <div data-field="restricciones" className={`mt-3 rounded-xl border bg-slate-50 p-4 ${fieldErrors.restricciones ? "border-red-400 ring-2 ring-red-100" : "border-slate-200"}`}>
                <p className="text-sm font-bold text-slate-900">
                  Restricciones del espacio
                </p>
                <p className="mt-1 text-sm leading-5 text-slate-600">
                  Si aceptas cualquier tipo de negocio, deja seleccionada la primera opción. Si no permites algún giro o actividad en tu propiedad, selecciona “Sí” y explica cuáles.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {[
                    { value: "no", label: "No tiene restricciones" },
                    { value: "si", label: "Sí tiene restricciones" },
                  ].map((option) => (
                    <label
                      key={option.value}
                      className={`cursor-pointer rounded-lg border px-3 py-2 text-sm font-bold ${
                        form.tieneRestricciones === option.value
                          ? "border-[#0056D2] bg-blue-50 text-blue-950"
                          : "border-slate-200 bg-white text-slate-700"
                      }`}
                    >
                      <input
                        type="radio"
                        name="tieneRestricciones"
                        value={option.value}
                        checked={form.tieneRestricciones === option.value}
                        onChange={(e) => updateForm("tieneRestricciones", e.target.value)}
                        className="mr-2"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                {form.tieneRestricciones === "si" && (
                  <textarea
                    required
                    placeholder="Ejemplo: no se permite comida con gas, no bares, no talleres ruidosos, no alcohol, no químicos o no duplicar giro de farmacia."
                    value={form.restricciones}
                    onChange={(e) => updateForm("restricciones", e.target.value)}
                    className="mt-3 w-full rounded-lg border border-slate-200 bg-white p-3 outline-none"
                    rows="4"
                  />
                )}
                {fieldError("restricciones")}
              </div>

              <div data-field="negociosMismaPlaza" className={`mt-3 rounded-xl border bg-orange-50 p-4 ${fieldErrors.negociosMismaPlaza ? "border-red-400 ring-2 ring-red-100" : "border-orange-100"}`}>
                <p className="text-sm font-black text-orange-950">
                  Entorno de tu propiedad *
                </p>
                <p className="mt-1 text-sm font-semibold leading-6 text-orange-900">
                  Escribe todo lo que puedas sobre el entorno de tu propiedad. Esto ayuda a que se rente más fácil porque el interesado entiende qué negocios ya existen, qué movimiento hay cerca y si su giro puede funcionar mejor.
                </p>
                <textarea
                  required
                  placeholder="Ejemplo: OXXO, farmacia, sushi, barbería, gimnasio, oficinas, abarrotes, restaurantes, escuelas, hospitales, avenida transitada o negocios parecidos cerca."
                  value={form.negociosMismaPlaza}
                  onChange={(e) => updateForm("negociosMismaPlaza", e.target.value)}
                  className="mt-3 w-full rounded-lg border border-orange-200 bg-white p-3 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                  rows="4"
                />
                {fieldError("negociosMismaPlaza")}
              </div>

              <div data-field="descripcion" className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-black text-slate-950">
                  Descripción general del espacio *
                </p>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Describe el espacio en general: estado, distribución, accesos, uso ideal y detalles que ayuden a entender la propiedad.
                </p>
                <textarea
                  required
                  minLength={40}
                  placeholder="Ejemplo: Local amplio en buen estado, con acceso cómodo para clientes y distribución adaptable para comercio, oficina o servicio."
                  value={form.descripcion}
                  onChange={(e) => updateForm("descripcion", e.target.value)}
                  className={fieldClass("descripcion", "mt-3 w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100")}
                  rows="4"
                />
                <p className={`mt-1 text-xs font-semibold ${
                  form.descripcion.trim().length >= 40 ? "text-emerald-600" : "text-slate-500"
                }`}>
                  {form.descripcion.trim().length}/40 caracteres mínimos
                </p>
                {fieldError("descripcion")}
              </div>

              <div data-field="ventajas" className={`mt-5 rounded-xl border bg-blue-50 p-4 ${fieldErrors.ventajas ? "border-red-400 ring-2 ring-red-100" : "border-blue-100"}`}>
                <p className="text-sm font-black text-blue-950">
                  Ventajas principales * (mínimo 3)
                </p>
                <p className="mt-1 text-sm leading-6 text-blue-900">
                  Escríbelas como frases cortas. Estas aparecerán acomodadas en la ficha como lista, no como párrafo.
                </p>
                <div className="mt-3 grid gap-2">
                  {Array.from({ length: Math.max(3, form.ventajas.length) }).map((_, index) => (
                    <div key={index} className="flex gap-2">
                      <span className="mt-3 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0056D2] text-xs font-black text-white">
                        {index + 1}
                      </span>
                      <input
                        type="text"
                        value={form.ventajas[index] || ""}
                        onChange={(e) => {
                          const nextAdvantages = [...form.ventajas];
                          nextAdvantages[index] = e.target.value;
                          updateForm("ventajas", nextAdvantages);
                        }}
                        placeholder={
                          [
                            "Ejemplo: Minisplit incluidos o totalmente refrigerado",
                            "Ejemplo: Sobre calle o blvd transitado",
                            "Ejemplo: Mantenimiento o algún servicio incluido",
                            "Ejemplo: Amplio estacionamiento para clientes",
                            "Ejemplo: Frente amplio para exhibición",
                          ][index] || "Otra ventaja importante"
                        }
                        className="w-full rounded-lg border border-blue-200 bg-white p-3 outline-none focus:border-[#0056D2] focus:ring-2 focus:ring-blue-100"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {form.ventajas.length < 10 && (
                    <button
                      type="button"
                      onClick={() => updateForm("ventajas", [...form.ventajas, ""])}
                      className="rounded-lg bg-white px-3 py-2 text-xs font-black text-[#0056D2] ring-1 ring-blue-200"
                    >
                      + Agregar otra ventaja
                    </button>
                  )}
                  {form.ventajas.length > 3 && (
                    <button
                      type="button"
                      onClick={() => updateForm("ventajas", form.ventajas.slice(0, -1))}
                      className="rounded-lg bg-white px-3 py-2 text-xs font-black text-slate-600 ring-1 ring-slate-200"
                    >
                      Quitar última
                    </button>
                  )}
                </div>
                <p className="mt-2 text-xs font-semibold text-blue-900">
                  Ejemplos: minisplit incluidos, totalmente refrigerado, calle o blvd transitado, mantenimiento o algún servicio incluido.
                </p>
                {fieldError("ventajas")}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
              <div className="mb-4 flex items-center gap-2">
                <Upload className="h-5 w-5 text-[#0056D2]" />
                <h2 className="font-bold text-slate-950">Fotos, videos y plano</h2>
              </div>
              <p className="mb-4 text-sm leading-6 text-slate-600">
                Las fotos son obligatorias para publicar.
              </p>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-black text-slate-950">
                  Fotografías recomendadas
                </p>
                <p className="mt-1 text-sm leading-5 text-slate-600">
                  Sube mínimo 3 fotografías. Entre más claras sean, más confianza genera tu publicación.
                </p>
                <ul className="mt-2 grid gap-1 text-sm text-slate-600 sm:grid-cols-2">
                  {photoGuide.map((item) => (
                    <li key={item}>- {item}</li>
                  ))}
                </ul>
              </div>

              <div data-field="imagenes" className={`mt-4 rounded-xl ${fieldErrors.imagenes ? "ring-2 ring-red-100" : ""}`}>
                <label className="mb-2 block text-sm font-bold text-slate-800">
                  Fotos del espacio * (mínimo 3, máximo 10)
                </label>
                <p className="mb-2 text-sm text-slate-500">
                  Incluye fachada, interior y acceso principal como base.
                </p>
                <input
                  required
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => appendLimitedFiles(setImages, e.target.files, 10)}
                  className={fieldClass("imagenes", "w-full rounded-lg border border-slate-200 p-3")}
                />
                {fieldError("imagenes")}
                <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
                  {images.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="relative aspect-square overflow-hidden rounded-lg border bg-slate-100">
                      <button
                        type="button"
                        onClick={() => removeFile(setImages, index)}
                        className="absolute right-1 top-1 z-10 h-6 w-6 rounded-full bg-black/70 text-xs text-white"
                      >
                        x
                      </button>
                      <img
                        src={buildFilePreview(file)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-2 flex items-start gap-2">
                  <Video className="mt-0.5 h-4 w-4 text-[#0056D2]" />
                  <div>
                    <label className="block text-sm font-bold text-slate-800">
                      Video exterior e interior (recomendado)
                    </label>
                    <p className="mt-1 text-sm leading-5 text-slate-500">
                      No es obligatorio, pero ayuda mucho a generar confianza y puede acelerar la renta o venta.
                    </p>
                  </div>
                </div>
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={(e) => appendLimitedFiles(setVideos, e.target.files, 2)}
                  className="w-full rounded-lg border border-slate-200 p-3"
                />
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {videos.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="relative overflow-hidden rounded-lg border">
                      <button
                        type="button"
                        onClick={() => removeFile(setVideos, index)}
                        className="absolute right-1 top-1 z-10 h-6 w-6 rounded-full bg-black/70 text-xs text-white"
                      >
                        x
                      </button>
                      <video
                        src={buildFilePreview(file)}
                        className="h-36 w-full object-cover"
                        controls
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-2 flex items-start gap-2">
                  <FileImage className="mt-0.5 h-4 w-4 text-[#0056D2]" />
                  <div>
                    <label className="block text-sm font-bold text-slate-800">
                      Plano, croquis o documento con medidas (opcional)
                    </label>
                    <p className="mt-1 text-sm leading-5 text-slate-500">
                      Si no lo tienes ahora, puedes dejarlo pendiente y subirlo después.
                    </p>
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  onChange={(e) => appendLimitedFiles(setPlans, e.target.files, 4)}
                  className="w-full rounded-lg border border-slate-200 p-3"
                />
                {plans.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-slate-600">
                    {plans.map((file, index) => (
                      <li key={`${file.name}-${index}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(setPlans, index)}
                          className="text-xs font-bold text-red-600"
                        >
                          Quitar
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-xl bg-[#F97316] p-4 font-black text-white shadow-sm transition hover:bg-orange-600 disabled:bg-orange-300"
          >
            {isSubmitting ? "Enviando propiedad..." : "Enviar propiedad"}
          </button>
        </form>
      </div>
      {showLogin && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 px-4">
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={() => setShowLogin(false)}
              aria-label="Cerrar inicio de sesion"
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50"
            >
              <X size={18} />
            </button>
            <h2 className="pr-10 text-2xl font-black text-slate-950">
              Inicia sesion para publicar
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Tus datos y archivos seguiran aqui. Al entrar, enviaremos la propiedad y te llevaremos a Mis propiedades.
            </p>
            <form onSubmit={handleLoginAndSubmit} className="mt-5 space-y-3">
              <input
                required
                type="email"
                autoComplete="username"
                placeholder="Correo electronico"
                value={loginEmail}
                onChange={(event) => setLoginEmail(event.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
              />
              <input
                required
                type="password"
                autoComplete="current-password"
                placeholder="Contrasena"
                value={loginPassword}
                onChange={(event) => setLoginPassword(event.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
              />
              {loginError && (
                <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">
                  {loginError}
                </p>
              )}
              <button
                type="submit"
                disabled={isLoggingIn}
                className="w-full rounded-lg bg-[#F97316] p-3 font-black text-white disabled:bg-orange-300"
              >
                {isLoggingIn ? "Iniciando sesion..." : "Iniciar sesion y publicar"}
              </button>
            </form>
          </div>
        </div>
      )}
      <FloatingWhatsApp />
    </div>
  );
};

export default Registro;

